import { useState, useCallback, useRef } from 'react';

const CACHE_KEY = 'cached_location';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const GEOLOCATION_TIMEOUT = 3000; // 3 secondes max pour location rapide

export function useOptimizedGeolocation() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  const getLocation = useCallback(async (options = {}) => {
    const {
      useCache = true,
      highAccuracy = false,
      timeout = GEOLOCATION_TIMEOUT
    } = options;

    setLoading(true);
    setError(null);

    try {
      // 1. Essayer le cache d'abord (rapide)
      if (useCache) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { coords, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;
            
            if (age < CACHE_DURATION) {
              setLoading(false);
              return { lat: coords.lat, lng: coords.lng, source: 'cache' };
            }
          } catch (e) {
            console.warn('Invalid cache, fetching fresh location');
          }
        }
      }

      // 2. Demander la position GPS
      if (!navigator.geolocation) {
        throw new Error('Geolocation not supported');
      }

      return await new Promise((resolve, reject) => {
        // Fallback timeout : si GPS prend trop longtemps, utiliser cache obsolète
        timeoutRef.current = setTimeout(() => {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            try {
              const { coords } = JSON.parse(cached);
              resolve({ 
                lat: coords.lat, 
                lng: coords.lng, 
                source: 'stale_cache' 
              });
            } catch (e) {
              reject(new Error('Location timeout'));
            }
          } else {
            reject(new Error('Location timeout'));
          }
        }, timeout);

        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(timeoutRef.current);
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            // Cache la nouvelle position
            localStorage.setItem(CACHE_KEY, JSON.stringify({
              coords: { lat, lng },
              timestamp: Date.now()
            }));

            resolve({ lat, lng, source: 'gps' });
          },
          (err) => {
            clearTimeout(timeoutRef.current);
            reject(err);
          },
          {
            enableHighAccuracy: highAccuracy,
            timeout: timeout,
            maximumAge: 0
          }
        );
      });
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
  }, []);

  return { getLocation, loading, error, clearCache };
}